import type { FC } from 'react';

import classNames from 'classnames';

import type { AssetsAssetSchemaTypedWithId } from '@iconik-io/iconik-api/src/services/assets';
import type {
  FilesFileSchemaWithId,
  FilesFormatSchemaWithId,
  FilesKeyframeSchemaWithIdNullable,
} from '@iconik-io/iconik-api/src/services/files';
import { Thumbnail, type ThumbnailSize } from '@iconik-io/uad';

import type { SearchItemEntity } from '../../../../redux/entities/normalized/search_item';
import { findBestKeyframe } from '../../../../utils/find_best_keyframe';
import { getFormatOrFileExtension } from '../../../../utils/format';
import { getObjectTypeFromAssetOrSearchItem } from '../../../../utils/get_object_type_from_asset_or_search_item';
import {
  MEDIA_TYPE_SUBCLIP,
  MEDIA_TYPE_VIDEO,
  nonMediaItemLabel,
} from '../../../../utils/media_type';
import { ImageScrub } from '../image_scrub/image_scrub';
import { CardItemWithoutKeyframesThumbnail } from '../item_card/card_item_without_keyframes/card_item_without_keyframes_thumbnail';

import styles from './scrubbable_thumbnail.module.css';

export const KEYFRAME_MAP = 'KEYFRAME_MAP';

type Props = {
  searchItem: SearchItemEntity | AssetsAssetSchemaTypedWithId;
  keyframes: FilesKeyframeSchemaWithIdNullable[] | undefined;
  imageScrub?: 'auto' | 'off';
  size?: ThumbnailSize;
  cardSize: 's' | 'm';
  formats?: FilesFormatSchemaWithId[];
  files?: FilesFileSchemaWithId[];
  rounded?: boolean;
};

export const ScrubbableThumbnail: FC<Props> = ({
  searchItem,
  keyframes,
  imageScrub = 'auto',
  size,
  cardSize,
  formats = [],
  files = [],
  rounded = false,
}) => {
  const keyframe = findBestKeyframe(keyframes ?? undefined);
  const keyframeMap = keyframes?.find(({ type }) => type === KEYFRAME_MAP);
  let isKeyframeMap =
    keyframeMap?.url && keyframeMap.status === 'CLOSED' && imageScrub !== 'off';

  const mediaType =
    'media_type' in searchItem ? searchItem.media_type : undefined;

  if (
    mediaType !== MEDIA_TYPE_VIDEO &&
    mediaType !== MEDIA_TYPE_SUBCLIP &&
    'object_type' in searchItem &&
    searchItem.format !== 'GIF'
  ) {
    isKeyframeMap = false;
  }

  const customKeyframe = keyframes?.find(
    (frame) =>
      frame.id === searchItem?.custom_keyframe && frame.status === 'CLOSED',
  );

  if (isKeyframeMap && keyframeMap && (customKeyframe || keyframe)) {
    return (
      <Thumbnail size={size}>
        <ImageScrub
          keyframeMapSrc={keyframeMap.url ?? undefined}
          keyframePosterSrc={customKeyframe?.url ?? keyframe?.url ?? undefined}
          objectTitle={searchItem.title}
          frameWidth={keyframeMap.resolution?.width}
          className={classNames({ [styles.rounded_thumbnail]: rounded })}
        />
      </Thumbnail>
    );
  }

  if (!keyframes?.length) {
    const itemFormat =
      nonMediaItemLabel(mediaType) ??
      getFormatOrFileExtension(searchItem, formats, files);

    return (
      <Thumbnail size={size}>
        <CardItemWithoutKeyframesThumbnail
          mediaType={itemFormat}
          cardSize={cardSize}
          objectType={getObjectTypeFromAssetOrSearchItem(searchItem)}
        />
      </Thumbnail>
    );
  }

  const imageUrl = customKeyframe?.url ?? keyframe?.url ?? undefined;
  return (
    <Thumbnail
      size={size}
      url={imageUrl}
      className={classNames({ [styles.rounded_thumbnail]: rounded })}
    />
  );
};
